import type { Gallery, GalleryLanguage, UpdateGalleryInput } from './gallery.api';
import { dateFromExpiresAt, expiresAtFromDate, parseSelectionLimit } from './gallery-view';

type SettingsGallery = Pick<
  Gallery,
  | 'title'
  | 'shootDate'
  | 'passwordEnabled'
  | 'password'
  | 'expiresAt'
  | 'autoDelete'
  | 'downloads'
  | 'language'
  | 'guestHints'
  | 'allowHiding'
  | 'selectionLimit'
>;

// The Settings dialog's fields as typed; dates as `YYYY-MM-DD`, the limit as text, the Gallery
// language '' for "as the site", the password '' for none. The look (style, layout, spacing, shape, cover) and the Watermark
// switch are the Design view's, which saves each change at once.
export interface GallerySettingsForm {
  title: string;
  shootDate: string;
  password: string;
  expiresDate: string;
  autoDelete: Gallery['autoDelete'];
  downloads: Gallery['downloads'];
  language: GalleryLanguage | '';
  guestHints: boolean;
  allowHiding: boolean;
  selectionLimit: string;
}

export function gallerySettingsForm(gallery: SettingsGallery): GallerySettingsForm {
  return {
    title: gallery.title,
    shootDate: gallery.shootDate ?? '',
    password: shownPassword(gallery),
    expiresDate: dateFromExpiresAt(gallery.expiresAt),
    autoDelete: gallery.autoDelete,
    downloads: gallery.downloads,
    language: gallery.language ?? '',
    guestHints: gallery.guestHints !== false,
    allowHiding: gallery.allowHiding === true,
    selectionLimit: gallery.selectionLimit ? String(gallery.selectionLimit) : '',
  };
}

// What the API takes for a gallery password (UpdateGalleryInput.password).
export const GALLERY_PASSWORD_MIN_LENGTH = 4;
export const GALLERY_PASSWORD_MAX_LENGTH = 64;

// The password as the field shows it: none while it is switched off.
function shownPassword(gallery: SettingsGallery): string {
  return gallery.passwordEnabled ? (gallery.password ?? '') : '';
}

// Only what changed goes to `updateGallery`, so an untouched expiry keeps its exact instant.
export function buildGallerySettingsInput(
  gallery: SettingsGallery,
  form: GallerySettingsForm
): { ok: true; input: UpdateGalleryInput } | { ok: false; error: 'selectionLimit' | 'password' } {
  const limit = parseSelectionLimit(form.selectionLimit);
  if (!limit.ok) {
    return { ok: false, error: 'selectionLimit' };
  }
  // An empty password switches it off; a new one must be long enough.
  const password = form.password.trim();
  if (password && password.length < GALLERY_PASSWORD_MIN_LENGTH) {
    return { ok: false, error: 'password' };
  }

  const input: UpdateGalleryInput = {};
  const title = form.title.trim();
  if (title !== gallery.title) {
    input.title = title;
  }
  const shootDate = form.shootDate.trim() || null;
  if (shootDate !== (gallery.shootDate ?? null)) {
    input.shootDate = shootDate;
  }
  if (form.expiresDate !== dateFromExpiresAt(gallery.expiresAt)) {
    input.expiresAt = expiresAtFromDate(form.expiresDate);
  }
  if (limit.value !== (gallery.selectionLimit ?? null)) {
    input.selectionLimit = limit.value;
  }
  if ((form.language || null) !== (gallery.language ?? null)) {
    input.language = form.language || null;
  }
  if (form.guestHints !== (gallery.guestHints !== false)) {
    input.guestHints = form.guestHints;
  }
  if (form.allowHiding !== (gallery.allowHiding === true)) {
    input.allowHiding = form.allowHiding;
  }
  if (password !== shownPassword(gallery)) {
    input.password = password;
  }
  for (const key of ['autoDelete', 'downloads'] as const) {
    if (form[key] !== gallery[key]) {
      (input as Record<string, unknown>)[key] = form[key];
    }
  }

  return { ok: true, input };
}
